/**
 * Angaben zur Bevollmächtigung für das BAFA-Antragsformular.
 *
 * Stellt der Berater den Antrag „als bevollmächtigte Person", fragt das Formular
 * VOR den Angaben zur antragstellenden Person einen eigenen Block ab (Organisation,
 * Ansprechperson, Anschrift, Telefon, E-Mail). Diese Daten stehen im Berater-Profil.
 *
 * Die Auswahlregel ist bewusst dieselbe wie beim Erzeugen der Vollmacht-PDF
 * (`api/src/bafa-forms/bafa-forms.service.ts`, Abschnitt 2): entweder die im Profil
 * hinterlegte abweichende Person oder das Berater-Profil selbst — und in beiden
 * Fällen schlägt die kundenspezifische Verteiler-E-Mail die Profil-Adresse.
 * Weichen die beiden Stellen voneinander ab, widersprechen sich Antrag und
 * Vollmacht des gleichen Vorgangs.
 */

export interface BevollmaechtigteProfil {
  contactPerson?: string;
  beraterAnrede?: string;
  phone?: string;
  email?: string;
  /** Team-Zugehörigkeit — entscheidet, ob der Inhaber als Bevollmächtigter auftritt. */
  teamId?: number | null;
  teamRole?: string | null;
  teamOwnerContactPerson?: string | null;
  teamOwnerBeraterAnrede?: string | null;
  teamOwnerPhone?: string | null;
  teamOwnerEmail?: string | null;
  companyName?: string;
  companyAddress?: string;
  companyHausnummer?: string;
  companyPlz?: string;
  companyOrt?: string;
  vollmachtOtherEnabled?: boolean;
  vollmachtOtherAnrede?: string;
  vollmachtOtherVorname?: string;
  vollmachtOtherNachname?: string;
  vollmachtOtherCompany?: string;
  vollmachtOtherAddress?: string;
  vollmachtOtherPlz?: string;
  vollmachtOtherOrt?: string;
  vollmachtOtherPhone?: string;
  vollmachtOtherEmail?: string;
}

export interface Bevollmaechtigte {
  organisation?: string;
  anrede?: string;
  vorname?: string;
  nachname?: string;
  land: string;
  plz?: string;
  ort?: string;
  strasse?: string;
  hausnummer?: string;
  telefonVorwahl?: string;
  telefonRufnummer?: string;
  email?: string;
}

/**
 * Zerlegt eine Telefonnummer in Vorwahl und Rufnummer, weil das BAFA-Formular
 * beides getrennt abfragt.
 *
 * - Steht im Profil ein Trennzeichen (Leerzeichen, „/", „-"), wird dort getrennt.
 * - Sonst nur bei deutschen MOBILfunknummern: Vorwahl = 0 + die drei Ziffern der
 *   Netzkennzahl (15x/16x/17x). Festnetz-Vorwahlen sind 2–5 Ziffern lang und
 *   ohne Verzeichnis nicht bestimmbar — dort bleibt die Vorwahl leer und die
 *   vollständige Nummer steht in der Rufnummer, statt sie falsch zu raten.
 */
export function splitPhone(phone?: string): { vorwahl?: string; rufnummer?: string } {
  const raw = (phone || '').trim();
  if (!raw) return {};

  // Deutsche Ländervorwahl zuerst durch die führende 0 ersetzen — sonst würde
  // „+49 177 5492227" am ersten Leerzeichen zu Vorwahl „+49" getrennt.
  const national = raw.replace(/^(?:\+49|0049)[\s/-]*/, '0');

  if (/[\s/-]/.test(national)) {
    const [vorwahlTeil, ...rest] = national.split(/[\s/-]+/).filter(Boolean);
    const rufnummer = rest.join('').replace(/\D/g, '');
    const vorwahl = normalisiereVorwahl(vorwahlTeil);
    if (vorwahl && vorwahl.length > 1 && rufnummer) return { vorwahl, rufnummer };
  }

  // Ohne Trennzeichen: nur Mobilfunk ist eindeutig zerlegbar (Netzkennzahl 15x/16x/17x).
  const ziffern = national.replace(/\D/g, '');
  const ohneNull = ziffern.startsWith('0') ? ziffern.slice(1) : null;

  if (ohneNull && /^1[5-7]\d/.test(ohneNull) && ohneNull.length > 3) {
    return { vorwahl: `0${ohneNull.slice(0, 3)}`, rufnummer: ohneNull.slice(3) };
  }

  return { rufnummer: raw };
}

/** „+49177" / „(0177)" → „0177"; leere/unbrauchbare Eingabe → undefined. */
function normalisiereVorwahl(teil: string): string | undefined {
  const ziffern = teil.replace(/[^\d+]/g, '');
  if (!ziffern) return undefined;
  if (ziffern.startsWith('+49')) return `0${ziffern.slice(3)}`;
  if (ziffern.startsWith('0049')) return `0${ziffern.slice(4)}`;
  if (ziffern.startsWith('0')) return ziffern;
  return `0${ziffern}`;
}

/**
 * Trennt „Clara-Schumann-Str. 2" in Straße und Hausnummer — die Vollmacht führt beides
 * in einem Feld, das Antragsformular fragt es getrennt ab. Ohne erkennbare Hausnummer
 * am Ende (z. B. „Am Markt") → undefined, damit der Aufrufer nichts erfindet.
 */
export function splitStrasse(strasse?: string): { strasse: string; hausnummer: string } | undefined {
  const s = (strasse || '').trim();
  if (!s) return undefined;
  const m = s.match(/^(.*?)[\s,]+(\d+\s*[a-zA-Z]?(?:\s*[-/]\s*\d+\s*[a-zA-Z]?)?)$/);
  if (!m || !m[1].trim()) return undefined;
  return { strasse: m[1].trim(), hausnummer: m[2].replace(/\s+/g, '') };
}

/** Trennt „Vorname Nachname" — erstes Wort Vorname, Rest Nachname (wie in der Vollmacht-PDF). */
function splitName(fullName?: string): { vorname?: string; nachname?: string } {
  const teile = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (teile.length === 0) return {};
  if (teile.length === 1) return { nachname: teile[0] };
  return { vorname: teile[0], nachname: teile.slice(1).join(' ') };
}

export function resolveBevollmaechtigte(
  profil: BevollmaechtigteProfil | null | undefined,
  customer?: { verteilerEmail?: string } | null,
): Bevollmaechtigte {
  const p = profil || {};

  // Bei Teammitgliedern tritt der Team-Inhaber als Bevollmächtigter auf — exakt die
  // Regel aus `bafa-forms.service.ts` („Für die Vollmacht: Team-Owner nehmen"). Der
  // Antrag MUSS dieselbe Person nennen wie die vom Kunden unterschriebene Vollmacht;
  // eine eigene Regel (z. B. „hat eigene BAFA-ID") würde die beiden auseinanderlaufen
  // lassen. Firmenname und Anschrift erbt das Profil bereits serverseitig vom Inhaber,
  // die persönlichen Felder kommen über teamOwner* dazu.
  const inhaberTrittAuf = !!p.teamId && p.teamRole !== 'owner';
  const person = inhaberTrittAuf
    ? {
        contactPerson: p.teamOwnerContactPerson || p.contactPerson,
        beraterAnrede: p.teamOwnerBeraterAnrede || p.beraterAnrede,
        phone: p.teamOwnerPhone || p.phone,
        email: p.teamOwnerEmail || p.email,
      }
    : { contactPerson: p.contactPerson, beraterAnrede: p.beraterAnrede, phone: p.phone, email: p.email };

  const basis = p.vollmachtOtherEnabled
    ? {
        organisation: p.vollmachtOtherCompany,
        anrede: p.vollmachtOtherAnrede,
        vorname: p.vollmachtOtherVorname,
        nachname: p.vollmachtOtherNachname,
        strasse: p.vollmachtOtherAddress,
        hausnummer: undefined as string | undefined,
        plz: p.vollmachtOtherPlz,
        ort: p.vollmachtOtherOrt,
        phone: p.vollmachtOtherPhone,
        email: p.vollmachtOtherEmail,
      }
    : {
        organisation: p.companyName,
        anrede: person.beraterAnrede,
        ...splitName(person.contactPerson),
        strasse: p.companyAddress,
        hausnummer: p.companyHausnummer,
        plz: p.companyPlz,
        ort: p.companyOrt,
        phone: person.phone,
        email: person.email,
      };

  const { vorwahl, rufnummer } = splitPhone(basis.phone);
  const verteiler = (customer?.verteilerEmail || '').trim();

  return {
    organisation: basis.organisation || undefined,
    anrede: basis.anrede || undefined,
    vorname: basis.vorname || undefined,
    nachname: basis.nachname || undefined,
    land: 'Deutschland',
    plz: basis.plz || undefined,
    ort: basis.ort || undefined,
    strasse: basis.strasse || undefined,
    hausnummer: basis.hausnummer || undefined,
    telefonVorwahl: vorwahl,
    telefonRufnummer: rufnummer,
    email: verteiler || basis.email || undefined,
  };
}
